import { defineStore } from 'pinia';
import { Ref, ref } from 'vue';
import api from './api';
import {
  FetchListOptions,
  SelectWithSearchOptions,
  Context,
  SetPageNumber,
  SetSelectedItems,
  SetFetchParams,
  SetSearchField,
  SetDisplayedItems,
  SetFetchingParams,
  SetHasMoreParams,
  SetTotalItemsParams,
} from './types/types';

export declare type StateByContext<T> = Partial<Record<Context, T>>;

declare type CustomerMgmtState = {
  fetchParams: StateByContext<FetchListOptions>;
  selectedItems: StateByContext<any[]>;
  displayedItems: StateByContext<any[]>;
  isFetching: StateByContext<boolean>;
  hasMore: StateByContext<boolean>;
  totalItems: StateByContext<number>;
};

export type NoticeType = 'error' | 'warning' | 'info' | 'success';
const useNoticeStore = defineStore('notice', () => {
  const active = ref(false);
  const type: Ref<NoticeType | null> = ref(null);
  const message: Ref<string | null> = ref(null);

  const reset = () => {
    active.value = false;
    type.value = null;
    message.value = null;
  };
  const show = (newType: NoticeType, newMessage: string) => {
    reset();
    active.value = true;
    type.value = newType;
    message.value = newMessage;
  };

  return { active, type, message, show, reset };
});

export default useNoticeStore;

export const useCustomerMgmtStore = (featureKey?: string) =>
  defineStore(`customer-mgmt-${featureKey ? featureKey : 'default'}`, () => {
    const fetchParams: Ref<CustomerMgmtState['fetchParams']> = ref({});
    const selectedItems: Ref<CustomerMgmtState['selectedItems']> = ref({});
    const displayedItems: Ref<CustomerMgmtState['displayedItems']> = ref({});
    const isFetching: Ref<CustomerMgmtState['isFetching']> = ref({});
    const hasMore: Ref<CustomerMgmtState['hasMore']> = ref({});
    const totalItems: Ref<CustomerMgmtState['totalItems']> = ref({});

    const getFetchParams = (context: Context) => fetchParams.value[context] ?? null;
    const getPageNumber = (context: Context) => fetchParams.value[context]?.page ?? 1;
    const getSearchField = (context: Context) => fetchParams.value[context]?.searchString ?? '';
    const getSelectedItems = (context: Context) => selectedItems.value[context] ?? [];
    const getDisplayedItems = (context: Context) => displayedItems.value[context] ?? [];
    const getIsFetching = (context: Context) => isFetching.value[context] ?? false;
    const getHasMore = (context: Context) => hasMore.value[context] ?? true;
    const getTotalItems = (context: Context) => totalItems.value[context] ?? null;

    function setFetchParams({ context, newFetchParams }: SetFetchParams) {
      fetchParams.value[context] = { ...getFetchParams(context), ...newFetchParams };

      return fetchParams.value[context];
    }

    function setPageNumber({ context, newPageNumber }: SetPageNumber) {
      const page: FetchListOptions['page'] = newPageNumber;

      fetchParams.value[context] = { ...getFetchParams(context), page };
    }

    function resetPageNumber(context: Context) {
      const page: FetchListOptions['page'] = 1;

      fetchParams.value[context] = { ...getFetchParams(context), page };
    }

    function setSearchField({ context, newSearchField }: SetSearchField) {
      const searchString: FetchListOptions['searchString'] = newSearchField;

      fetchParams.value[context] = { ...getFetchParams(context), searchString };
    }

    function setSelectedItems({ context, items }: SetSelectedItems) {
      selectedItems.value[context] = items;
    }

    function setDisplayedItems({ context, newDisplayedItems }: SetDisplayedItems) {
      displayedItems.value[context] = newDisplayedItems;
    }

    function appendToDisplayedItems({ context, newDisplayedItems }: SetDisplayedItems) {
      displayedItems.value[context] = [...(displayedItems.value[context] ?? []), ...newDisplayedItems];
    }

    function resetDisplayedItems(context: Context) {
      if (Object.prototype.hasOwnProperty.call(displayedItems.value, context)) {
        displayedItems.value[context] = [];
      }
    }

    function setIsFetching({ context, isFetchingNow }: SetFetchingParams) {
      isFetching.value[context] = isFetchingNow;
    }

    function setHasMore({ context, moreItemsAvailable }: SetHasMoreParams) {
      hasMore.value[context] = moreItemsAvailable;
    }

    function setTotalItems({ context, itemsAvailable }: SetTotalItemsParams) {
      totalItems.value[context] = itemsAvailable;
    }

    function resetToDefaults(context: Context) {
      if (Object.prototype.hasOwnProperty.call(displayedItems.value, context)) {
        displayedItems.value[context] = [];
      }
      if (Object.prototype.hasOwnProperty.call(selectedItems.value, context)) {
        selectedItems.value[context] = [];
      }
      if (Object.prototype.hasOwnProperty.call(hasMore.value, context)) {
        hasMore.value[context] = true;
      }
      if (Object.prototype.hasOwnProperty.call(totalItems.value, context)) {
        delete totalItems.value[context];
      }
    }

    /**
     * Main functions
     */
    async function fetchItems(context: Context, options: FetchListOptions) {
      const apiMethod = `fetch${context.charAt(0).toUpperCase() + context.slice(1)}` as keyof typeof api.fetchers;

      if (!Object.prototype.hasOwnProperty.call(api.fetchers, apiMethod)) {
        console.error(`No api method with name - ${apiMethod}`);

        return Promise.resolve(undefined);
      }

      const response = await api.fetchers[apiMethod](options);
      return response.data ?? { data: [], meta: [] };
    }

    async function getItemByUid(context: Context, uid: string) {
      const apiMethod = `get${context.charAt(0).toUpperCase() + context.slice(1)}` as keyof typeof api.getters;

      if (!Object.prototype.hasOwnProperty.call(api.getters, apiMethod)) {
        console.error(`No api method with name - ${apiMethod}`);

        return Promise.resolve(undefined);
      }

      // @ts-ignore
      let desiredObject = getDisplayedItems(context).find((item) => item.uid === uid);

      if (desiredObject === undefined) {
        const response = await api.getters[apiMethod](uid);

        desiredObject = response.data;
      }

      return desiredObject;
    }

    async function fetchData(context: Context) {
      setIsFetching({ context, isFetchingNow: true });

      // const { page, itemsPerPage } = getFetchParams(context);
      const reqParam = getFetchParams(context);
      const page = reqParam!.page!;
      const itemsPerPage = reqParam!.itemsPerPage!;
      const { data, meta } = await fetchItems(context, {
        ...getFetchParams(context),
        page: getPageNumber(context),
        searchString: getSearchField(context),
      });
      // Backend always returns page 1, keep until fixed...
      const currentPage = page ?? meta.pageNumber;
      const currentPerPage = meta.pageSize ?? itemsPerPage;
      setTotalItems({ context, itemsAvailable: meta.totalItems });
      setHasMore({
        context,
        moreItemsAvailable: currentPage * currentPerPage < meta.totalItems,
      });
      setIsFetching({ context, isFetchingNow: false });

      return data;
    }

    async function fetchPreSelected(options: SelectWithSearchOptions) {
      if (options.preSelectedIds === undefined || options.preSelectedIds.length < 1) {
        return;
      }

      setIsFetching({ context: options.context, isFetchingNow: true });

      const filters: FetchListOptions['filters'] = { uids: options.preSelectedIds };
      const { data } = await fetchItems(options.context, { ...getFetchParams(options.context), filters });

      appendToDisplayedItems({ context: options.context, newDisplayedItems: data });

      if (data.length > 0) {
        setSelectedItems({ context: options.context, items: data });
      }

      setIsFetching({ context: options.context, isFetchingNow: false });
    }

    async function cleanAndFetch(options: SelectWithSearchOptions) {
      resetDisplayedItems(options.context);
      const newDisplayedItems = await fetchData(options.context);

      setDisplayedItems({ context: options.context, newDisplayedItems });
    }

    async function fetchSearch(options: SelectWithSearchOptions) {
      resetDisplayedItems(options.context);
      resetPageNumber(options.context);

      const fetchedItems = await fetchData(options.context);
      const alreadySelected = Array.isArray(getSelectedItems(options.context))
        ? getSelectedItems(options.context)
        : [getSelectedItems(options.context)];
      const newDisplayedItems = [...alreadySelected, ...fetchedItems];

      resetDisplayedItems(options.context);
      appendToDisplayedItems({ context: options.context, newDisplayedItems });
    }

    async function infiniteScrollIntersect(
      options: SelectWithSearchOptions,
      entries: unknown,
      observer: unknown,
      isIntersecting: boolean,
    ) {
      if (isIntersecting && !getIsFetching(options.context)) {
        const newDisplayedItems = await fetchData(options.context);

        appendToDisplayedItems({ context: options.context, newDisplayedItems });
        setPageNumber({ context: options.context, newPageNumber: (getFetchParams(options.context)?.page || 0) + 1 });
      }

      return Promise.resolve(null);
    }

    return {
      fetchParams,
      selectedItems,
      displayedItems,
      isFetching,
      hasMore,
      totalItems,
      setFetchParams,
      setPageNumber,
      resetPageNumber,
      setSearchField,
      setSelectedItems,
      resetDisplayedItems,
      setIsFetching,
      setHasMore,
      setTotalItems,
      resetToDefaults,
      fetchItems,
      getItemByUid,
      fetchData,
      fetchPreSelected,
      cleanAndFetch,
      fetchSearch,
      infiniteScrollIntersect,
      // Getters
      getFetchParams,
      getPageNumber,
      getSearchField,
      getSelectedItems,
      getDisplayedItems,
      getIsFetching,
      getHasMore,
      getTotalItems,
    };
  })();
